import { useEffect, useState, useCallback } from 'react';
import { useLoading } from '../../../../../../hooks/useLoading';
import { loadMmsSetupData, ICharityMmsSetup } from '../../../../../../utils/firebase/mmsSetup';

interface UseMmsSetupDataProps {
  charityId: string | undefined;
}

interface UseMmsSetupDataReturn {
  loading: boolean;
  mmsSetupData: ICharityMmsSetup | null;
  error: string | null;
  refresh: () => Promise<void>;
}

const MINIMUM_LOADING_DURATION_MS = 800;

export function useMmsSetupData({
  charityId,
}: UseMmsSetupDataProps): UseMmsSetupDataReturn {
  const { loading, runWithLoading, setLoading } = useLoading(true);
  const [mmsSetupData, setMmsSetupData] = useState<ICharityMmsSetup | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!charityId) {
      setMmsSetupData(null);
      return;
    }

    try {
      setError(null);
      const data = await loadMmsSetupData(charityId);
      setMmsSetupData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load MMS setup data';
      console.error('Failed to load MMS setup data:', err);
      setError(errorMessage);
    }
  }, [charityId]);

  useEffect(() => {
    if (!charityId) {
      setLoading(false);
      return;
    }

    runWithLoading(refresh, MINIMUM_LOADING_DURATION_MS);
  }, [charityId, runWithLoading, setLoading, refresh]);

  return {
    loading,
    mmsSetupData,
    error,
    refresh,
  };
}
